import { isAxiosError } from 'axios';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { StyledEditDialog } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import { toaster } from 'components/v2/toast';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { LCASoftwareModel, LCASoftwareVersionModel } from 'services/api/adminLCASoftwareApi';
import {
  useCreateLCASoftware,
  useCreateLCASoftwareVersion,
  useDeleteLCASoftware,
  useDeleteLCASoftwareVersion,
  useUpdateLCASoftware,
  useUpdateLCASoftwareVersion,
} from 'services/api/mutations';
import { useLCASoftwareAdmin } from 'services/api/queries';
import { Container } from 'styles/Styles.styled';
import { H1NewUI, FormPanel } from 'styles/v2/Styles.styled';
import styled from 'styled-components';

interface SoftwareFormData {
  id: string;
  name: string;
  versionRequired: boolean;
}

interface VersionFormData {
  id: string;
  name: string;
}

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0;
`;

const GridPanel = styled(FormPanel)`
  display: block;
  padding: 0;
  overflow: hidden;
`;

const VersionsPanel = styled.div`
  padding: 1rem 1.5rem;
  background: ${(props) => props.theme.colors.primaryWhite};
`;

const VersionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  h4 {
    margin: 0;
    ${(props) => props.theme.typography.headerCaptionSemiBold}
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-weight: 600;
    font-size: 13px;
    color: ${(props) => props.theme.colors.primaryBlack};
  }

  small {
    font-size: 11px;
    color: #999;
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 12px;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

function compareVersionNames(a: { name: string }, b: { name: string }): number {
  const partsA = a.name.split('.');
  const partsB = b.name.split('.');
  const maxLen = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < maxLen; i++) {
    const partA = i < partsA.length ? partsA[i] : '0';
    const partB = i < partsB.length ? partsB[i] : '0';
    const numA = parseInt(partA, 10);
    const numB = parseInt(partB, 10);
    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA !== numB) return numA - numB;
    } else {
      const cmp = partA.localeCompare(partB, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

const AdminLCASoftware: React.FunctionComponent = () => {
  const [showSoftwareDialog, setShowSoftwareDialog] = useState(false);
  const [editingSoftware, setEditingSoftware] = useState<SoftwareFormData>({ id: '', name: '', versionRequired: false });
  const [editingSoftwareId, setEditingSoftwareId] = useState<string | null>(null);

  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [editingVersion, setEditingVersion] = useState<VersionFormData>({ id: '', name: '' });
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editingVersionSoftwareId, setEditingVersionSoftwareId] = useState<string>('');

  const [expandedRows, setExpandedRows] = useState<LCASoftwareModel[]>([]);

  const query = useLCASoftwareAdmin();
  const createSoftware = useCreateLCASoftware();
  const updateSoftware = useUpdateLCASoftware();
  const deleteSoftwareMutation = useDeleteLCASoftware();
  const createVersion = useCreateLCASoftwareVersion();
  const updateVersion = useUpdateLCASoftwareVersion();
  const deleteVersionMutation = useDeleteLCASoftwareVersion();

  const software = query.data;

  // Software CRUD

  const openAddSoftware = () => {
    setEditingSoftware({ id: '', name: '', versionRequired: false });
    setEditingSoftwareId(null);
    setShowSoftwareDialog(true);
  };

  const openEditSoftware = (row: LCASoftwareModel) => {
    setEditingSoftware({ id: row.id, name: row.name, versionRequired: row.versionRequired });
    setEditingSoftwareId(row.id);
    setShowSoftwareDialog(true);
  };

  const handleSaveSoftware = () => {
    if (!editingSoftware.name.trim()) {
      toaster({ severity: 'error', summary: 'Validation', details: 'Name is required' });
      return;
    }

    const softwarePayload = {
      name: editingSoftware.name,
      versionRequired: editingSoftware.versionRequired,
      ...(editingSoftware.id.trim() ? { id: editingSoftware.id.trim() } : {}),
    };

    if (editingSoftwareId) {
      updateSoftware.mutate(
        { id: editingSoftwareId, ...softwarePayload },
        {
          onSuccess: () => {
            setShowSoftwareDialog(false);
            toaster({ severity: 'success', summary: 'Success', details: 'LCA Software updated' });
          },
          onError: () => {
            toaster({ severity: 'error', summary: 'Error', details: 'Failed to update LCA Software' });
          },
        }
      );
    } else {
      createSoftware.mutate(softwarePayload, {
        onSuccess: () => {
          setShowSoftwareDialog(false);
          toaster({ severity: 'success', summary: 'Success', details: 'LCA Software created' });
        },
        onError: () => {
          toaster({ severity: 'error', summary: 'Error', details: 'Failed to create LCA Software' });
        },
      });
    }
  };

  const handleDeleteSoftware = (row: LCASoftwareModel) => {
    confirmDelete({
      caption: `Are you sure you want to delete "${row.name}"?`,
      description: (
        <div>
          This LCA Software and all its versions will be permanently deleted.
          <br /> You will not be able to recover it.
        </div>
      ),
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: () =>
        deleteSoftwareMutation.mutate(row.id, {
          onSuccess: () => {
            toaster({ severity: 'success', summary: 'Success', details: 'LCA Software deleted' });
          },
          onError: (error) => {
            if (isAxiosError(error) && error.response?.status === 409) {
              toaster({
                severity: 'error',
                summary: 'The LCA Software could not be deleted.',
                details: 'It is currently used in one or more EPDs.',
              });
              return;
            }
            toaster({ severity: 'error', summary: 'Error', details: 'Failed to delete LCA Software' });
          },
        }),
    });
  };

  // Version CRUD

  const openAddVersion = (softwareId: string) => {
    setEditingVersion({ id: '', name: '' });
    setEditingVersionId(null);
    setEditingVersionSoftwareId(softwareId);
    setShowVersionDialog(true);
  };

  const openEditVersion = (softwareId: string, version: LCASoftwareVersionModel) => {
    setEditingVersion({ id: version.id, name: version.name });
    setEditingVersionId(version.id);
    setEditingVersionSoftwareId(softwareId);
    setShowVersionDialog(true);
  };

  const handleSaveVersion = () => {
    if (!editingVersion.name.trim()) {
      toaster({ severity: 'error', summary: 'Validation', details: 'Version name is required' });
      return;
    }

    const versionPayload = {
      name: editingVersion.name,
      ...(editingVersion.id.trim() ? { id: editingVersion.id.trim() } : {}),
    };

    if (editingVersionId) {
      updateVersion.mutate(
        { softwareId: editingVersionSoftwareId, versionId: editingVersionId, ...versionPayload },
        {
          onSuccess: () => {
            setShowVersionDialog(false);
            toaster({ severity: 'success', summary: 'Success', details: 'Version updated' });
          },
          onError: () => {
            toaster({ severity: 'error', summary: 'Error', details: 'Failed to update version' });
          },
        }
      );
    } else {
      createVersion.mutate(
        { softwareId: editingVersionSoftwareId, ...versionPayload },
        {
          onSuccess: () => {
            setShowVersionDialog(false);
            toaster({ severity: 'success', summary: 'Success', details: 'Version created' });
          },
          onError: () => {
            toaster({ severity: 'error', summary: 'Error', details: 'Failed to create version' });
          },
        }
      );
    }
  };

  const handleDeleteVersion = (softwareId: string, version: LCASoftwareVersionModel) => {
    confirmDelete({
      caption: `Are you sure you want to delete version "${version.name}"?`,
      description: (
        <div>
          This version will be permanently deleted.
          <br /> You will not be able to recover it.
        </div>
      ),
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: () =>
        deleteVersionMutation.mutate(
          { softwareId, versionId: version.id },
          {
            onSuccess: () => {
              toaster({ severity: 'success', summary: 'Success', details: 'Version deleted' });
            },
            onError: (error) => {
              if (isAxiosError(error) && error.response?.status === 409) {
                toaster({
                  severity: 'error',
                  summary: 'The version could not be deleted.',
                  details: 'It is currently used in one or more EPDs.',
                });
                return;
              }
              toaster({ severity: 'error', summary: 'Error', details: 'Failed to delete version' });
            },
          }
        ),
    });
  };

  // Templates

  const versionRequiredTemplate = (row: LCASoftwareModel) => {
    return <InputSwitch checked={row.versionRequired} disabled />;
  };

  const versionsCountTemplate = (row: LCASoftwareModel) => {
    return <span>{row.lcaSoftwareVersions?.length ?? 0}</span>;
  };

  const softwareActionsTemplate = (row: LCASoftwareModel) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-plus"
          className="p-button-rounded p-button-text p-button-success"
          tooltip="Add version"
          tooltipOptions={{ position: 'top' }}
          onClick={() => openAddVersion(row.id)}
        />
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={() => openEditSoftware(row)} />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDeleteSoftware(row)}
        />
      </div>
    );
  };

  const versionActionsTemplate = (version: LCASoftwareVersionModel) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => openEditVersion(version.lcaSoftwareId, version)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDeleteVersion(version.lcaSoftwareId, version)}
        />
      </div>
    );
  };

  const rowExpansionTemplate = (row: LCASoftwareModel) => {
    const versions = [...(row.lcaSoftwareVersions || [])].sort(compareVersionNames);
    return (
      <VersionsPanel>
        <VersionsHeader>
          <h4>Versions of {row.name}</h4>
          <Button
            label="Add version"
            icon="pi pi-plus"
            className="p-button-sm p-button-outlined"
            onClick={() => openAddVersion(row.id)}
          />
        </VersionsHeader>
        <DataTableStyled value={versions} emptyMessage="No versions" size="small">
          <Column field="name" header="Version Name" sortable />
          <Column body={versionActionsTemplate} style={{ width: '8rem' }} />
        </DataTableStyled>
      </VersionsPanel>
    );
  };

  if (query.isLoading) {
    return (
      <Container>
        <ProgressSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <ConfirmDialog />
      <PageHeader>
        <H1NewUI>LCA Software</H1NewUI>
        <Button label="Add software" icon="pi pi-plus" className="p-button-sm" onClick={openAddSoftware} />
      </PageHeader>

      <GridPanel>
        <DataTableStyled
          value={software || []}
          paginator
          rows={20}
          sortField="name"
          sortOrder={1}
          emptyMessage="No LCA software found"
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data as LCASoftwareModel[])}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="id"
        >
          <Column expander style={{ width: '3rem' }} />
          <Column field="id" header="ID" sortable style={{ minWidth: '20rem' }} hidden />
          <Column field="name" header="Name" sortable style={{ minWidth: '14rem' }} />
          <Column
            field="versionRequired"
            header="Version Required"
            body={versionRequiredTemplate}
            sortable
            style={{ minWidth: '10rem' }}
          />
          <Column header="Versions" body={versionsCountTemplate} style={{ minWidth: '6rem' }} />
          <Column body={softwareActionsTemplate} style={{ minWidth: '10rem' }} />
        </DataTableStyled>
      </GridPanel>

      {/* Software dialog */}
      <StyledEditDialog
        visible={showSoftwareDialog}
        isOpened={showSoftwareDialog}
        header={editingSoftwareId ? 'Edit LCA Software' : 'Add LCA Software'}
        style={{ width: '500px' }}
        onHide={() => setShowSoftwareDialog(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowSoftwareDialog(false)}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={handleSaveSoftware}
              loading={createSoftware.isPending || updateSoftware.isPending}
            />
          </div>
        }
      >
        <FormGrid>
          <FormField>
            <label htmlFor="softwareId">ID</label>
            <InputText
              id="softwareId"
              value={editingSoftware.id}
              onChange={(e) => setEditingSoftware({ ...editingSoftware, id: e.target.value })}
              disabled={!!editingSoftwareId}
              placeholder="Leave empty to auto-generate"
            />
          </FormField>
          <FormField>
            <label htmlFor="softwareName">Name *</label>
            <InputText
              id="softwareName"
              value={editingSoftware.name}
              onChange={(e) => setEditingSoftware({ ...editingSoftware, name: e.target.value })}
            />
          </FormField>
          <SwitchRow>
            <InputSwitch
              id="versionRequired"
              checked={editingSoftware.versionRequired}
              onChange={(e) => setEditingSoftware({ ...editingSoftware, versionRequired: e.value ?? false })}
            />
            <label htmlFor="versionRequired">Version Required</label>
          </SwitchRow>
        </FormGrid>
      </StyledEditDialog>

      {/* Version dialog */}
      <StyledEditDialog
        visible={showVersionDialog}
        isOpened={showVersionDialog}
        header={editingVersionId ? 'Edit Version' : 'Add Version'}
        style={{ width: '500px' }}
        onHide={() => setShowVersionDialog(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowVersionDialog(false)}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={handleSaveVersion}
              loading={createVersion.isPending || updateVersion.isPending}
            />
          </div>
        }
      >
        <FormGrid>
          <FormField>
            <label htmlFor="versionId">ID</label>
            <InputText
              id="versionId"
              value={editingVersion.id}
              onChange={(e) => setEditingVersion({ ...editingVersion, id: e.target.value })}
              disabled={!!editingVersionId}
              placeholder="Leave empty to auto-generate"
            />
          </FormField>
          <FormField>
            <label htmlFor="versionName">Version Name *</label>
            <InputText
              id="versionName"
              value={editingVersion.name}
              onChange={(e) => setEditingVersion({ ...editingVersion, name: e.target.value })}
            />
          </FormField>
        </FormGrid>
      </StyledEditDialog>
    </Container>
  );
};

export default withGreyBackground(AdminLCASoftware);
